import React, { useRef } from "react";
import axios from "axios";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import { NodeProps, useReactFlow } from "reactflow";
import { Main, Styled } from "./style";
import { webURL } from "../../../../../services/API";
import { AuthenticateSelect } from "../../../../auth/select";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";
import { updateNodeValue } from "../../../../../services/method";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";

type whatsappListenerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

export interface RtnWhatsAppMessage {
  phoneNumber?: string | null;
  body: string;
  fromName: string;
  to: string;
  timestamp: number | string | null;
  isGroup: boolean;
  groupName?: string;
  hasMedia: boolean;
  mediaType?: string;
}

export function WhatsappListener({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: whatsappListenerProps) {
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const wasExpanded = React.useRef(false);

  // const [isCopied, setIsCopied] = React.useState(false);
  const [connectionStatus, setConnectionStatus] = React.useState<
    | "not_initialized"
    | "qr_pending"
    | "connecting"
    | "connected"
    | "disconnected"
    | "failed"
  >("not_initialized");
  const [qrCode, setQrCode] = React.useState<string>("");
  const [phoneNumber, setPhoneNumber] = React.useState<string>("");
  const statusCheckInterval = useRef<NodeJS.Timeout | null>(null);

  const extractQrImage = (payload: any): string =>
    payload?.qrDataUrl || payload?.qrCodeDataUrl || payload?.qrCode || "";

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => setIsExpanded((p) => !p));
    if (setChildSettings)
      setChildSettings(() =>
        setSettings((p) => ({ ...p, isToggle: !p.isToggle })),
      );
  }, [setChildMinimize, setChildSettings]);

  // Copy to clipboard
  // const handleCopy = (text: string) => {
  //   navigator.clipboard.writeText(text);
  //   setIsCopied(true);
  //   setTimeout(() => setIsCopied(false), 2000);
  // };

  // Initialize WhatsApp connection
  const initializeConnection = async (connectionId: string) => {
    try {
      console.log("[WhatsApp] Initializing connection:", connectionId);
      const response = await axios.post(
        `${webURL}/triggers/whatsapp/initialize`,
        { connectionId },
      );

      if (response.data.status === "qr_pending") {
        setQrCode(extractQrImage(response.data));
        setConnectionStatus("qr_pending");
        registerListener(connectionId);
        startStatusChecking(connectionId);
      } else if (response.data.status === "connected") {
        setConnectionStatus("connected");
        setPhoneNumber(response.data.phoneNumber || "");
        registerListener(connectionId);
      }
    } catch (error) {
      console.error("[WhatsApp] Initialize error:", error);
      setConnectionStatus("failed");
    }
  };

  // Check connection status periodically
  const startStatusChecking = (connectionId: string) => {
    if (statusCheckInterval.current) {
      clearInterval(statusCheckInterval.current);
    }

    statusCheckInterval.current = setInterval(async () => {
      try {
        const response = await axios.get(
          `${webURL}/triggers/whatsapp/status?connectionId=${connectionId}`,
        );

        if (response.data.status === "connected") {
          setConnectionStatus("connected");
          setPhoneNumber(response.data.phoneNumber || "");
          setQrCode("");
          if (statusCheckInterval.current) {
            clearInterval(statusCheckInterval.current);
          }
          registerListener(connectionId);
        } else if (
          response.data.status === "qr_pending" &&
          response.data.qrDataUrl
        ) {
          setQrCode(response.data.qrDataUrl);
        }
      } catch (error) {
        console.error("[WhatsApp] Status check error:", error);
      }
    }, 3000); // Check every 3 seconds
  };

  // Register workflow as a listener for this WhatsApp connection
  const registerListener = async (connectionId: string) => {
    try {
      // Get projectId from URL query parameters
      const urlParams = new URLSearchParams(window.location.search);
      const projectId = urlParams.get("id");

      if (!projectId) {
        console.error("[WhatsApp] No project ID found in URL");
        return;
      }

      await axios.post(`${webURL}/triggers/whatsapp/register-listener`, {
        connectionId,
        projectId,
        nodeId: id,
        nodeIndex: data.index,
      });
      console.log("[WhatsApp] Listener registered successfully");
    } catch (error) {
      console.error("[WhatsApp] Register listener error:", error);
    }
  };

  // Handle connection selection
  const handleConnectionChange = async (connection: any) => {
    if (!connection) return;

    console.log("[WhatsApp] Connection selected:", connection);
    const connectionId = connection._id || connection.id;

    if (!connectionId) {
      console.error(
        "[WhatsApp] No connection ID found in connection object:",
        connection,
      );
      return;
    }

    updateNodeValue(id, setNodes, { connection, connectionId });

    // Check status first instead of initializing
    try {
      console.log("[WhatsApp] Checking status for connection:", connectionId);
      const response = await axios.get(
        `${webURL}/triggers/whatsapp/status?connectionId=${connectionId}`,
      );

      console.log("[WhatsApp] Status response:", response.data);

      if (response.data.status === "connected") {
        setConnectionStatus("connected");
        setPhoneNumber(response.data.phoneNumber || "");
        registerListener(connectionId);
      } else if (response.data.status === "qr_pending") {
        setConnectionStatus("qr_pending");
        setQrCode(extractQrImage(response.data));
        registerListener(connectionId);
        startStatusChecking(connectionId);
      } else if (
        response.data.status === "not_initialized" ||
        response.data.status === "disconnected" ||
        response.data.status === "failed" ||
        response.data.status === "requires_rescan"
      ) {
        // Initialize if not connected
        initializeConnection(connectionId);
      } else {
        setConnectionStatus(response.data.status || "not_initialized");
      }
    } catch (error) {
      console.error("[WhatsApp] Status check error:", error);
      // If status check fails, try to initialize
      initializeConnection(connectionId);
    }
  };

  // Cleanup interval on unmount
  React.useEffect(() => {
    return () => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current);
      }
    };
  }, []);

  // Check connection status on mount and when connection changes
  React.useEffect(() => {
    const checkStatus = async () => {
      const connectionId =
        data.value?.connectionId ||
        data.value?.connection?._id ||
        data.value?.connection?.id;

      if (connectionId) {
        try {
          console.log(
            "[WhatsApp] Checking status on mount for connection:",
            connectionId,
          );
          const response = await axios.get(
            `${webURL}/triggers/whatsapp/status?connectionId=${connectionId}`,
          );

          console.log("[WhatsApp] Status on mount:", response.data);

          if (response.data.status === "connected") {
            setConnectionStatus("connected");
            setPhoneNumber(response.data.phoneNumber || "");
            registerListener(connectionId);
          } else if (response.data.status === "qr_pending") {
            setConnectionStatus("qr_pending");
            setQrCode(extractQrImage(response.data));
            registerListener(connectionId);
            startStatusChecking(connectionId);
          } else if (
            response.data.status === "not_initialized" ||
            response.data.status === "requires_rescan"
          ) {
            initializeConnection(connectionId);
          } else {
            setConnectionStatus(response.data.status || "not_initialized");
          }
        } catch (error) {
          console.error("[WhatsApp] Status check error:", error);
          setConnectionStatus("not_initialized");
        }
      }
    };

    checkStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.value?.connectionId, data.value?.connection?._id]);

  React.useEffect(() => {
    if (isExpanded && !wasExpanded.current && data.value?.connection) {
      wasExpanded.current = true;
      handleConnectionChange(data.value.connection);
    }
    if (!isExpanded) {
      wasExpanded.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, data.value?.connection]);

  const storedConnectionId =
    data.value?.connectionId ||
    data.value?.connection?._id ||
    data.value?.connection?.id;
  const hasConnection = !!storedConnectionId;

  const isWarning = !hasConnection || connectionStatus === "failed";
  const isConnected = connectionStatus === "connected";

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon">
              <IoWarningOutline size={21} />
            </span>
          </>
        ) : isConnected ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}

        {isExpanded && (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "400px" }}>
            <Styled>
              <div className="section">
                <div className="label">WhatsApp Connection</div>
                <AuthenticateSelect
                  type="whatsapp"
                  defaultId={storedConnectionId}
                  handleAuthenticate={(type, connection) => {
                    if (type === "add") {
                      handleConnectionChange(connection);
                    } else {
                      updateNodeValue(id, setNodes, {
                        connection: null,
                        connectionId: null,
                      });
                      setConnectionStatus("not_initialized");
                      setQrCode("");
                      setPhoneNumber("");
                    }
                  }}
                  withLine={hasConnection}
                  background={data.background}
                />
              </div>

              {hasConnection && (
                <>
                  <div className="section">
                    <div className="label">Connection Status</div>
                    <div className="status-container">
                      {connectionStatus === "qr_pending" && (
                        <div className="qr-section">
                          <div className="status-text">
                            Scan QR Code with WhatsApp
                          </div>
                          {qrCode && (
                            <div className="qr-code">
                              <img src={qrCode} alt="WhatsApp QR Code" />
                            </div>
                          )}
                          <div className="helper-text">
                            1. Open WhatsApp on your phone
                            <br />
                            2. Tap Menu or Settings and select "Linked Devices"
                            <br />
                            3. Tap "Link a Device"
                            <br />
                            4. Point your phone at this screen to scan the code
                          </div>
                        </div>
                      )}

                      {connectionStatus === "connecting" && (
                        <div className="status-text">Connecting...</div>
                      )}

                      {connectionStatus === "connected" && (
                        <div className="status-connected">
                          <div className="status-icon">✓</div>
                          <div>
                            <div className="status-text">Connected</div>
                            {phoneNumber && (
                              <div className="phone-number">{phoneNumber}</div>
                            )}
                          </div>
                        </div>
                      )}

                      {connectionStatus === "disconnected" && (
                        <div className="status-text error">Disconnected</div>
                      )}

                      {connectionStatus === "failed" && (
                        <div className="status-text error">
                          Connection Failed
                        </div>
                      )}

                      {connectionStatus === "not_initialized" && (
                        <div className="status-text">Not initialized</div>
                      )}
                    </div>
                  </div>

                  <div className="section">
                    <div className="label">Trigger Information</div>
                    <div className="info-box">
                      <p>
                        This trigger will activate the workflow whenever a
                        WhatsApp message is received on the connected account.
                      </p>
                      <p>
                        The message data passed to workflow nodes is limited to:
                        phoneNumber, body, fromName, to, timestamp, isGroup,
                        groupName, hasMedia, mediaType.
                      </p>
                    </div>
                  </div>
                </>
              )}
            </Styled>
          </ExpandedBlock>
        )}
      </Main>
    </>
  );
}